// src/events/create_event_form1.js
import React, { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiX,
  FiType,
  FiFolder,
  FiLayers,
  FiChevronDown,
  FiArrowRightCircle,
} from "react-icons/fi";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

/* ──────────────────────────────────────────────────────────
   Brand / Theme
   ────────────────────────────────────────────────────────── */
const COLORS = {
  primary: "#f20c53",
  secondary: "#fb6c33",
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  page: "#f5f6f8",
  card: "#ffffff",
};

/* ──────────────────────────────────────────────────────────
   Helpers
   ────────────────────────────────────────────────────────── */
function getSessionMobile() {
  try {
    const raw = localStorage.getItem("events_session");
    const j = raw ? JSON.parse(raw) : null;
    const m = j?.mobile || j?.phone || "";
    return String(m).replace(/\D/g, "").slice(-10);
  } catch {
    return "";
  }
}

/** Remove empties and simple "na"/"no" markers */
function cleanList(list) {
  return (list || [])
    .map((s) => String(s || "").trim())
    .filter(Boolean)
    .filter((s) => !/^na$|^no$/i.test(s));
}

/* ──────────────────────────────────────────────────────────
   Category Tree
   - Includes every parent you supplied; added practical subcats
   - Title Case labels; "Other" last with “Create Sub Category”
   ────────────────────────────────────────────────────────── */
const CATEGORY_TREE = {
  /* Popular */
  "Comedy Shows": cleanList([
    "Standup Comedy",
    "Improv Comedy",
    "Open Mic Comedy",
    "Roast",
    "Sketch",
  ]),
  "Food & Drinks": cleanList([
    "Buffet",
    "Fine Dining",
    "Food Festival",
    "Pub Hopping",
    "Street Food",
    "Sunday Brunch",
    "Wine Tasting",
    "Beer Pairing",
    "Chef’s Table",
  ]),
  "Music Shows": cleanList([
    "Concerts",
    "Club Gigs",
    "Open Mic",
    "Music Festivals",
    "Tribute Night",
  ]),
  Theatre: cleanList([
    "Adaptation",
    "Adult",
    "Adventure",
    "Biography",
    "Broadway",
    "Classic",
    "Comedy",
    "Contemporary",
    "Drama",
    "Fantasy",
    "Historical",
    "Horror",
    "Musical",
    "Mystery",
    "Mythological",
    "Noir",
    "Online Streaming Plays",
    "Period",
    "Political",
    "Psychological",
    "Regional",
    "Romantic",
    "Sci-Fi",
    "Suspense",
    "Thriller",
    "Tragedy",
    "Tribute",
    "War",
    "Action",
  ]),
  Workshops: cleanList([
    "Food & Drinks",
    "Theatre",
    "Acting",
    "Arts & Crafts",
    "Books & Literature",
    "Business",
    "Comedy",
    "Dance",
    "Education",
    "Environment",
    "Fashion & Beauty",
    "Film Making",
    "Gaming",
    "Gardening",
    "Health & Fitness",
    "Home Decor",
    "Jewellery Workshops",
    "Magic Workshops",
    "Masterclass",
    "Music",
    "Painting",
    "Performing Arts",
    "Photography",
    "Poetry",
    "Sports",
    "Storytelling",
    "Technology",
    "Upskill",
    "Wellness",
    "Writing",
    "Yoga",
  ]),

  /* All */
  Adventure: cleanList([
    "Adventure Parks",
    "Biking",
    "Camping",
    "Cruise",
    "Cycling Tour",
    "Jet Ski",
    "Kayaking",
    "Motorcycle Tour",
    "Paragliding",
    "Rafting",
    "Safari",
    "Sailing",
    "Scuba Diving",
    "Skiing",
    "Snowboarding",
    "Surfing",
    "Trekking",
    "Water Sports",
    "Ziplining",
  ]),
  "Amusement Parks": cleanList(["Theme Parks", "Water Parks", "Snow Parks", "Resorts", "Adventure Zones"]),
  "Antiques, Heritage & Museums": cleanList([
    "Archaeology",
    "City Museum",
    "Art Museum",
    "Heritage Walk",
    "Special Exhibition",
  ]),
  Archery: cleanList(["Coaching", "Open Shoot", "Competition", "Beginner Workshop"]),
  Athletics: cleanList([
    "Sprint",
    "Relay Race",
    "High Jump",
    "Long Jump",
    "Javelin Throw",
    "Discus Throw",
    "Shot Put",
    "Hammer Throw",
    "Track Meet",
  ]),
  "Award Shows": cleanList(["Film Awards", "Business Awards", "Felicitation", "Performance Night"]),
  Badminton: cleanList(["Coaching", "Open Play", "Doubles Tournament", "Singles Tournament"]),
  Baseball: cleanList(["Friendly Match", "Coaching", "League Game"]),
  Basketball: cleanList(["Pickup Games", "3x3 Tournament", "Coaching", "League Match"]),
  "Beer Festival": cleanList(["Craft Beer Fest", "Tap Takeover", "Beer & Food Pairing"]),
  "Boat Race": cleanList(["Regatta", "Dragon Boat", "Traditional Race"]),
  "Body Building": cleanList(["Amateur Show", "Pro Qualifier", "Seminar"]),
  Bowling: cleanList(["Open Lane", "League Night", "Tournament"]),
  Boxing: cleanList(["Amateur Bouts", "Pro Fight Night", "Coaching Clinic"]),
  "Card Games": cleanList(["Poker", "Bridge", "Rummy", "Teen Patti", "MTG/TCG Meetup"]),
  "Celebrity Wishes": cleanList(["Birthday Wish", "Anniversary Wish", "Custom Shoutout"]),
  Chess: cleanList(["Simul", "Rapid", "Blitz", "Open Tournament", "Coaching"]),
  "Christmas Celebrations": cleanList([
    "Xmas Parties",
    "Xmas Dinner Experience",
    "Xmas Live Performances",
    "Xmas Nature Trails",
    "Xmas Amusement Park",
    "Xmas Staycation",
    "Xmas Unique Experiences",
    "Xmas Getaway",
  ]),
  Conferences: cleanList([
    "Business",
    "Comics",
    "Design",
    "Education",
    "Environment",
    "Fashion & Beauty",
    "Health Care",
    "Health, Fitness & Wellbeing",
    "LGBTQ",
    "Science",
    "Technology",
    "Startup & VC",
    "Marketing",
    "Product",
  ]),
  Cricket: cleanList(["T20", "T10", "ODI", "Test Cricket", "World Cup Screening", "Box Cricket"]),
  Cruises: cleanList(["Dinner Cruise", "Sundowner Cruise", "Party Cruise", "River Cruise"]),
  Cycling: cleanList(["City Ride", "Endurance", "Downhill", "Gravel", "Night Ride", "Cyclothon"]),
  "Durga Puja Festival": cleanList(["Pandal Hopping", "Cultural Night", "Bhog & Prasad", "Sindoor Khela"]),
  Exhibitions: cleanList([
    "Antiques",
    "Art Exhibitions",
    "Automobile",
    "Comics",
    "Education",
    "Fashion",
    "Films",
    "Furniture",
    "Handicrafts",
    "Heritage",
    "Jewellery",
    "Market",
    "Painting",
    "Pets",
    "Photography",
    "Shopping",
    "Technology",
    "Trade Shows",
    "Real Estate",
  ]),
  Experimental: cleanList(["Immersive Art", "Site-Specific", "New Media", "Avant-garde"]),
  Festivals: cleanList(["Cultural", "Food", "Music", "Film", "Literature", "Seasonal"]),
  Football: cleanList(["5-a-side", "7-a-side", "League", "Screening", "Coaching"]),
  Futsal: cleanList(["Open Play", "Tournament", "League", "Coaching"]),
  Gaming: cleanList([
    "Board Games",
    "Bowling",
    "Escape Games",
    "Laser Tag",
    "Paintball",
    "Go Karting",
    "Indoor Cricket",
    "VR Gaming",
    "Simulation",
    "LAN Party",
  ]),
  Golf: cleanList(["Driving Range", "9-Hole", "18-Hole", "Putting Clinic", "Tournament"]),
  "Hilsa Festival": cleanList(["Food Fest", "Tasting Menu", "Chef’s Special"]),
  Hockey: cleanList(["League", "Friendly", "Coaching"]),
  "Holi Celebrations": cleanList(["Color Run", "Foam Party", "Rain Dance", "Family Carnival"]),
  "Holi Parties": cleanList(["Bhangra", "Blues", "Bollywood", "EDM", "Rock", "Sufi"]),
  "Horse Race": cleanList(["Derby Day", "Race Day Experience", "Paddock Tour"]),
  Hospitals: cleanList(["Health Camp", "Blood Donation", "Awareness Talk"]),
  "Human Pyramid": cleanList(["Dahi Handi Competition", "Training"]),
  "Ice Skating": cleanList(["Open Skate", "Figure Skating", "Lessons", "Showcase"]),
  "Improv Theatre": cleanList([
    "Adaptation",
    "Adult",
    "Adventure",
    "Biography",
    "Comedy",
    "Contemporary",
    "Drama",
    "Fantasy",
    "Historical",
    "Horror",
    "Musical",
    "Mystery",
    "Mythological",
    "Period",
    "Political",
    "Psychological",
    "Romantic",
    "Sci-Fi",
    "Suspense",
    "Thriller",
    "Tragedy",
    "War",
    "Broadway",
    "Classic",
    "Noir",
    "Online Streaming Plays",
    "Regional",
    "Tribute",
  ]),
  "Interactive Theatre": cleanList([
    "Adaptation",
    "Adult",
    "Adventure",
    "Biography",
    "Comedy",
    "Contemporary",
    "Drama",
    "Fantasy",
    "Historical",
    "Horror",
    "Musical",
    "Mystery",
    "Mythological",
    "Period",
    "Political",
    "Psychological",
    "Romantic",
    "Sci-Fi",
    "Suspense",
    "Thriller",
    "Tragedy",
    "War",
    "Broadway",
    "Classic",
    "Noir",
    "Online Streaming Plays",
    "Regional",
    "Tribute",
  ]),
  Judo: cleanList(["Coaching", "Belt Test", "Tournament"]),
  Kabaddi: cleanList(["Circle Style", "League", "Coaching"]),
  Karate: cleanList(["Kata Workshop", "Sparring", "Grading", "Tournament"]),
  Kayaking: cleanList(["River Kayaking", "Sea Kayaking", "Whitewater", "Tours", "Workshop"]),
  "Kho Kho": cleanList(["League", "Friendly", "Coaching"]),
  Kickboxing: cleanList(["Cardio Kickboxing", "Sparring", "Tournament"]),
  Kids: cleanList(["Competition", "Education", "Entertainment", "Hobby Classes", "Summer Camps"]),
  Meetups: cleanList(["Startup", "Tech", "Photography", "Writers", "Book Club", "Travelers", "Moms & Dads"]),
  Mime: cleanList(["Solo Mime", "Ensemble Mime", "Workshop"]),
  "Mixed Martial Arts": cleanList(["Amateur Bouts", "Pro Card", "Workshop"]),
  Monologue: cleanList(["Acting Showcase", "Competition", "Workshop"]),
  Monuments: cleanList(["Guided Tour", "Photography Walk", "Night Tour"]),
  Motorsports: cleanList(["AutoCross", "Drag", "Track Day", "Rally", "Karting Championship"]),
  "Muay Thai": cleanList(["Coaching", "Sparring", "Fight Night"]),
  "Multi Sport": cleanList(["Corporate Olympics", "School Sports Day", "Community Sports Fest"]),
  "Navratri Celebration": cleanList(["Garba Night", "Dandiya Night", "Cultural Show"]),
  "New Year Parties": cleanList(["Gala Dinner", "Beach Party", "Club NYE", "Family NYE"]),
  Nightlife: cleanList(["DJ Night", "Ladies Night", "Sundowner", "Theme Night", "Live Music"]),
  "Non-verbal": cleanList(["Movement Theatre", "Mime", "Physical Comedy"]),
  "Online Games": cleanList(["E-Tournament", "Casual Lobby", "Streamer Event"]),
  "Online Sports": cleanList(["Fantasy League", "eFootball", "eRacing"]),
  "Online Streaming Plays": cleanList(["Live Stream", "Recorded Stream", "Interactive Stream"]),
  Padel: cleanList(["Open Play", "Doubles League", "Coaching", "Tournament"]),
  Parties: cleanList(["Theme Party", "Foam Party", "Pool Party", "Silent Disco"]),
  Performances: cleanList(["Dance", "Music", "Theatre", "Spoken Word"]),
  Pickleball: cleanList(["Open Play", "Doubles", "Coaching", "Tournament"]),
  "Poila Baishakh Festival": cleanList(["Cultural Show", "Food Mela", "Concert"]),
  Polo: cleanList(["Match Day", "Coaching", "Exhibition Match"]),
  Pool: cleanList(["8-Ball", "9-Ball", "Handicap League", "Coaching"]),
  Puppetry: cleanList(["Traditional", "Contemporary", "Workshop", "Puppet Making"]),
  "Quizzes & Competitions": cleanList(["Pub Quiz", "School Quiz", "Open Quiz", "Trivia Night"]),
  "Role Play": cleanList(["Murder Mystery", "D&D/TT RPG", "LARP"]),
  Rowing: cleanList(["Regatta", "Coaching", "Indoor Rowing"]),
  Rugby: cleanList(["Sevens", "Fifteens", "Coaching", "League"]),
  Running: cleanList(["5K", "10K", "Half Marathon", "Marathon", "Trail Run"]),
  Sailing: cleanList(["Learn to Sail", "Weekend Sail", "Regatta"]),
  Screening: cleanList(["Movie Premiere", "Short Films", "Documentary", "Match Screening"]),
  Shooting: cleanList(["10m Air Rifle", "Pistol", "Coaching", "Competition"]),
  "Shops & Malls": cleanList(["Flea Market", "Pop-up Store", "Mall Event", "Festival Bazaar"]),
  Skating: cleanList(["Inline Skating", "Roller Derby", "Figure Skating", "Coaching"]),
  Snooker: cleanList(["Open Table", "Handicap", "Pro-Am", "Coaching"]),
  Spirituality: cleanList(["Meditation", "Satsang", "Retreat", "Talk"]),
  Squash: cleanList(["Open Play", "Ladder", "Coaching", "Tournament"]),
  "Street Plays": cleanList(["Nukkad Natak", "Outreach Show", "Workshop"]),
  Swimming: cleanList(["Coaching", "Lap Swim", "Competition", "Aqua Zumba"]),
  "Table Tennis": cleanList(["Open Play", "Coaching", "Tournament"]),
  Talks: cleanList(["Motivational", "Author Talk", "Tech Talk", "Panel Discussion"]),
  Tennis: cleanList(["Open Play", "Coaching", "Singles", "Doubles Tournament"]),
  "Tourist Attractions": cleanList(["City Tour", "Museum Pass", "Theme Attractions", "Sightseeing"]),
  Triathlon: cleanList(["Sprint", "Olympic", "Half Iron", "Training Camp"]),
  "Tug of War": cleanList(["Corporate", "Community", "School"]),
  "Unique Tours": cleanList([
    "Heritage Walk",
    "Food Trail",
    "Haunted Walk",
    "Street Art Tour",
    "Cycling Tour",
    "Slum Tour",
  ]),

  /* Always last */
  Other: cleanList(["Create Sub Category"]),
};

const CATEGORY_ORDER = Object.keys(CATEGORY_TREE).sort((a, b) => {
  if (a === "Other") return 1;
  if (b === "Other") return -1;
  return a.localeCompare(b);
});

/* ──────────────────────────────────────────────────────────
   Page
   ────────────────────────────────────────────────────────── */
export default function CreateEventForm1() {
  const nav = useNavigate();

  // Modal open by default
  const [open, setOpen] = useState(true);

  // Form state
  const [eventName, setEventName] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [customSubCategory, setCustomSubCategory] = useState("");

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Derived
  const subcats = useMemo(() => {
    const raw = CATEGORY_TREE[category] || [];
    return cleanList(raw);
  }, [category]);

  const requiresSubCat =
    category === "Other" || (Array.isArray(subcats) && subcats.length > 0);

  const effectiveSubCat =
    category === "Other" ? (customSubCategory || "").trim() : subCategory;

  const canProceed =
    eventName.trim().length >= 3 &&
    category &&
    (!requiresSubCat || effectiveSubCat);

  // UX: Reset sub when main category changes
  function onChangeCategory(next) {
    setCategory(next);
    setSubCategory("");
    setCustomSubCategory("");
  }

  async function handleProceed(e) {
    e.preventDefault();
    if (!canProceed || saving) return;

    setErr("");
    setSaving(true);

    try {
      const createdByMobile = getSessionMobile();
      const payload = {
        name: eventName.trim(),
        category,
        subCategory: effectiveSubCat || null,
        status: "draft",
        createdByMobile: createdByMobile || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        step: "form1",
      };

      const ref = await addDoc(collection(db, "create_new_event"), payload);

      // Redirect immediately with the new id
      nav(`/events/create_event_form2?eid=${encodeURIComponent(ref.id)}`, { replace: true });
    } catch (e2) {
      console.error(e2);
      setErr("Could not create the event. Please try again.");
      setSaving(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: COLORS.page }}>
      {/* Header strip (brand) */}
      <HeaderBar />

      {/* Centered modal overlay */}
      {open && (
        <div style={overlay} role="dialog" aria-modal="true" onClick={() => setOpen(false)}>
          <div
            style={modalCard}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top strip (no logo per request) */}
            <div style={modalTop}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, color: COLORS.text }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    display: "grid",
                    placeItems: "center",
                    border: `1px solid ${COLORS.border}`,
                    color: COLORS.primary,
                    background: "#fff",
                  }}
                >
                  <FiFolder />
                </div>
                <div style={{ fontWeight: 900, fontSize: 20 }}>
                  Create Event
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                style={iconButton}
                title="Close"
              >
                <FiX />
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleProceed} style={{ display: "grid", gap: 16 }}>
              {/* Event Name */}
              <div>
                <label style={labelStyle}>
                  <FiType />
                  Name of the event
                </label>
                <input
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  placeholder="e.g., Sunset Unplugged at The Bay"
                  style={inputStyle}
                />
                <div style={{ marginTop: 6, fontSize: 13, color: COLORS.subtext }}>
                  Choose a clear, unique title. Descriptive names boost discovery and SEO.
                </div>
              </div>

              {/* Main Category (custom dropdown opens below) */}
              <div>
                <label style={labelStyle}>
                  <FiFolder />
                  Select event category
                </label>
                <Dropdown
                  value={category}
                  onChange={onChangeCategory}
                  options={CATEGORY_ORDER}
                  placeholder="Choose a category…"
                />
              </div>

              {/* Sub-category (only when needed or for Other) */}
              {requiresSubCat && (
                <div>
                  <label style={labelStyle}>
                    <FiLayers />
                    {category === "Other" ? "Create sub category" : "Select event sub-category"}
                  </label>

                  {category === "Other" ? (
                    <input
                      value={customSubCategory}
                      onChange={(e) => setCustomSubCategory(e.target.value)}
                      placeholder="Type your sub-category"
                      style={inputStyle}
                    />
                  ) : (
                    <Dropdown
                      value={subCategory}
                      onChange={setSubCategory}
                      options={subcats}
                      placeholder="Choose a sub-category…"
                    />
                  )}
                </div>
              )}

              {/* Error */}
              {err && (
                <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 2 }}>{err}</div>
              )}

              {/* Proceed */}
              <div style={{ display: "grid", placeItems: "center", marginTop: 4 }}>
                <button
                  type="submit"
                  disabled={!canProceed || saving}
                  style={{
                    ...primaryBtn,
                    opacity: !canProceed || saving ? 0.6 : 1,
                    cursor: !canProceed || saving ? "not-allowed" : "pointer",
                    minWidth: 220,
                  }}
                >
                  <FiArrowRightCircle />
                  {saving ? "Saving…" : "Proceed"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   Custom Dropdown (opens below, scrolls inside popup)
   ────────────────────────────────────────────────────────── */
function Dropdown({ value, onChange, options, placeholder = "Select…" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const display = value || "";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Field */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          ...inputStyle,
          width: "100%",
          textAlign: "left",
          display: "grid",
          gridTemplateColumns: "1fr 20px",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <span style={{ color: display ? COLORS.text : COLORS.subtext }}>
          {display || placeholder}
        </span>
        <FiChevronDown style={{ justifySelf: "end", opacity: 0.65 }} />
      </button>

      {/* Menu — always below the field, not covering it */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 8,
            background: "#fff",
            border: `1px solid ${COLORS.border}`,
            borderRadius: 10,
            boxShadow: "0 10px 24px rgba(0,0,0,.12)",
            maxHeight: "40vh",
            overflowY: "auto",
            zIndex: 5,
          }}
        >
          {options.length === 0 && (
            <div style={{ padding: "10px 12px", color: COLORS.subtext, fontSize: 14 }}>
              No options
            </div>
          )}
          {options.map((opt) => (
            <div
              key={opt}
              role="option"
              aria-selected={opt === value}
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              style={{
                padding: "10px 12px",
                fontSize: 14,
                color: COLORS.text,
                background: opt === value ? "rgba(242,12,83,.06)" : "#fff",
                cursor: "pointer",
                borderBottom: `1px solid ${COLORS.border}`,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(15,23,42,.04)")}
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  opt === value ? "rgba(242,12,83,.06)" : "#fff")
              }
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   Small Parts / Styles
   ────────────────────────────────────────────────────────── */
function HeaderBar() {
  return (
    <div
      style={{
        height: 72,
        background: "#1f2937",
        borderBottom: "1px solid rgba(255,255,255,.08)",
        color: "#fff",
        display: "grid",
        gridTemplateColumns: "1fr",
        alignItems: "center",
        padding: "0 16px",
      }}
    >
      <div
        style={{
          textAlign: "center",
          fontWeight: 900,
          fontSize: 24, // a bit bigger
          letterSpacing: 0.2,
        }}
      >
        Create Your Event
      </div>
    </div>
  );
}

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.45)",
  display: "grid",
  placeItems: "center",
  padding: 16,
  zIndex: 100,
};

const modalCard = {
  width: "100%",
  maxWidth: 720,
  maxHeight: "80vh", // popup height capped
  overflowY: "auto", // scroll inside popup
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 16,
  boxShadow: "0 18px 40px rgba(0,0,0,.25)",
  padding: 16,
};

const modalTop = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingBottom: 8,
  borderBottom: `1px solid ${COLORS.border}`,
  marginBottom: 12,
};

const inputStyle = {
  width: "100%",
  height: 48, // a little taller
  padding: "0 12px",
  marginTop: 6,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  outline: "none",
  background: "#fff",
  color: COLORS.text,
  fontSize: 15, // bigger text
};

const labelStyle = {
  fontSize: 15, // bigger labels
  color: COLORS.text,
  display: "inline-grid",
  gridAutoFlow: "column",
  gap: 8,
  alignItems: "center",
};

const iconButton = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  height: 38,
  width: 38,
  borderRadius: 8,
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
};

const primaryBtn = {
  height: 50,
  padding: "0 18px",
  borderRadius: 999,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  fontSize: 15,
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
};
